import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { assets } from "../lib/assets";
import {
  GACHA_REWARDS,
  claimDailyGachaReward,
  loadDailyGachaState,
  loadSurpriseLetter,
  recordSurpriseInteraction,
  type DailyGachaState,
  type GachaDrawResult,
  type RewardDefinition,
  type SurpriseLetterInstance
} from "../services/surprise/surpriseRepository";
import { useAppStore } from "../store/useAppStore";

type MachineStage = "idle" | "spinning" | "dropping" | "ready" | "opening" | "revealed" | "exhausted";
type SurpriseView = "gacha" | "letter";

const imageAssets: Record<string, string> = {
  rewardStar: assets.gachaRewardStars,
  starLantern: assets.gachaRewardMoonLamp,
  dress: assets.gachaRewardDress,
  telescope: assets.gachaRewardTelescope,
  moonPlant: assets.moonPlant,
  starCushion: assets.starCushion,
  cloudBunny: assets.cloudBunny
};

const emptyState: DailyGachaState = { date: "", drawsUsed: 0, maxDraws: 3, rewards: [], source: "local" };
const wait = (milliseconds: number) => new Promise((resolve) => window.setTimeout(resolve, milliseconds));

function RewardVisual({ reward, compact = false }: { reward: Pick<RewardDefinition, "assetKey" | "name" | "quantity" | "type">; compact?: boolean }) {
  const image = imageAssets[reward.assetKey];
  if (image) return <img className={compact ? "reward-asset compact" : "reward-asset"} src={image} alt={reward.name} />;
  if (reward.assetKey === "dress") return <span className="css-prize dress" role="img" aria-label={reward.name}><i /><b>✦</b></span>;
  if (reward.assetKey === "cape") return <span className="css-prize cape" role="img" aria-label={reward.name}><i /><b>✦</b></span>;
  return <span className="css-prize telescope" role="img" aria-label={reward.name}><i /><b /></span>;
}

function LetterExperience({ childId, onClose }: { childId: string | null; onClose: () => void }) {
  const [letter, setLetter] = useState<SurpriseLetterInstance | null>(null);
  const [opened, setOpened] = useState(false);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    void loadSurpriseLetter(childId, assets.teacherOpeningLetter).then(setLetter);
    return () => { audioRef.current?.pause(); audioRef.current = null; };
  }, [childId]);

  const openLetter = () => {
    setOpened(true);
    void recordSurpriseInteraction(childId, "surprise.letter_opened", { template_id: letter?.templateId });
  };

  const toggleAudio = async () => {
    if (!letter?.audioUrl) return;
    if (audioRef.current && playing) {
      audioRef.current.pause();
      setPlaying(false);
      return;
    }
    const audio = audioRef.current ?? new Audio(letter.audioUrl);
    audioRef.current = audio;
    audio.onended = () => setPlaying(false);
    audio.onerror = () => setPlaying(false);
    try {
      await audio.play();
      setPlaying(true);
      void recordSurpriseInteraction(childId, "surprise.letter_audio_played", { template_id: letter.templateId });
    } catch { setPlaying(false); }
  };

  return (
    <section className="surprise-letter-template page-view" aria-label="星光信件">
      <button className="letter-close" type="button" onClick={onClose} aria-label="返回扭蛋机">‹</button>
      <header>
        <p>今日小惊喜 · 星光邮局</p>
        <h1>{letter?.title ?? "星光信件"}</h1>
        <span>文字与老师音频已经保存，下次打开还是这一封。</span>
      </header>
      <div className={"react-letter-scene " + (opened ? "is-open" : "")}>
        <button className="react-envelope" type="button" onClick={openLetter} disabled={opened} aria-label="打开信封">
          <span className="react-envelope-back" />
          <span className="react-envelope-paper" />
          <span className="react-envelope-front" />
          <span className="react-envelope-flap" />
          <b>✦</b>
          <small>轻轻点开信封</small>
        </button>
        {letter && (
          <article className="react-letter" aria-hidden={!opened}>
            <p className="letter-recipient">{letter.recipient}：</p>
            {letter.paragraphs.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
            <footer>{letter.signature}</footer>
            <button className={playing ? "is-playing" : ""} type="button" onClick={toggleAudio}>{playing ? "暂停讲述" : "▶ 听老师读信"}</button>
          </article>
        )}
      </div>
    </section>
  );
}

export function SurprisePage() {
  const { activeChildId, stars, setStars } = useAppStore();
  const [view, setView] = useState<SurpriseView>("gacha");
  const [daily, setDaily] = useState<DailyGachaState>(emptyState);
  const [stage, setStage] = useState<MachineStage>("idle");
  const [pendingReward, setPendingReward] = useState<GachaDrawResult | null>(null);
  const [revealedReward, setRevealedReward] = useState<GachaDrawResult | null>(null);
  const [leverAngle, setLeverAngle] = useState(0);
  const [message, setMessage] = useState("转动摇杆，看看今天的惊喜吧！");
  const [loading, setLoading] = useState(true);
  const dragStart = useRef<number | null>(null);
  const busy = ["spinning", "dropping", "opening"].includes(stage);

  useEffect(() => {
    let alive = true;
    void loadDailyGachaState(activeChildId)
      .then((state) => {
        if (!alive) return;
        setDaily(state);
        if (state.drawsUsed >= state.maxDraws) {
          setStage("exhausted");
          setMessage("今天的三份惊喜已经收好啦，明天再见！");
        }
        void recordSurpriseInteraction(activeChildId, "surprise.gacha_opened", { draws_used: state.drawsUsed });
      })
      .catch((error) => {
        if (!alive) return;
        console.error("Unable to load daily surprise", error);
        setDaily({ ...emptyState, date: new Date().toISOString().slice(0, 10) });
        setMessage("云端奖池正在重新连接，请稍后再试，已有奖励不会丢失。");
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [activeChildId]);

  const beginDraw = async () => {
    if (busy || stage === "ready" || stage === "opening" || daily.drawsUsed >= 3) return;
    setRevealedReward(null);
    setPendingReward(null);
    setStage("spinning");
    setLeverAngle(78);
    setMessage("扭蛋正在星光里转呀转……");

    const claim = claimDailyGachaReward(activeChildId, stars);
    try {
      await wait(1250);
      setStage("dropping");
      setMessage("听！有一颗扭蛋掉下来啦！");
      const reward = await claim;
      setPendingReward(reward);
      await wait(720);
      setStage("ready");
      setLeverAngle(0);
      setMessage("轻轻点一下扭蛋，把惊喜打开吧！");
    } catch (error) {
      setStage("idle");
      setLeverAngle(0);
      setMessage(error instanceof Error ? error.message : "星光走丢了一小会儿，请再试一次。");
    }
  };

  const openCapsule = async () => {
    if (stage !== "ready" || !pendingReward) return;
    setStage("opening");
    setMessage("三、二、一——惊喜出现！");
    await wait(760);
    setRevealedReward(pendingReward);
    setStars(pendingReward.totalStars);
    const nextRewards = daily.rewards.some((reward) => reward.clientDrawId === pendingReward.clientDrawId)
      ? daily.rewards
      : [...daily.rewards, pendingReward];
    const nextDaily = { ...daily, drawsUsed: Math.max(daily.drawsUsed, pendingReward.drawIndex), rewards: nextRewards };
    setDaily(nextDaily);
    setStage("revealed");
    setMessage(pendingReward.type === "stars" ? "小星光已经飞进你的星光袋！" : "“" + pendingReward.name + "”已经送到我的小屋！");
  };

  const finishReveal = () => {
    setPendingReward(null);
    setRevealedReward(null);
    if (daily.drawsUsed >= 3) {
      setStage("exhausted");
      setMessage("今天的三份惊喜都收好啦，明天再来看看吧！");
    } else {
      setStage("idle");
      setMessage("还可以转 " + (3 - daily.drawsUsed) + " 次，下一颗会是什么呢？");
    }
  };

  const onLeverDown = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (busy || stage !== "idle") return;
    dragStart.current = event.clientY;
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const onLeverMove = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (dragStart.current === null || !event.currentTarget.hasPointerCapture(event.pointerId)) return;
    setLeverAngle(Math.min(78, Math.max(0, (event.clientY - dragStart.current) * 1.25)));
  };

  const onLeverUp = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (dragStart.current === null) return;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    dragStart.current = null;
    if (leverAngle >= 35) void beginDraw();
    else setLeverAngle(0);
  };

  if (view === "letter") return <LetterExperience childId={activeChildId} onClose={() => setView("gacha")} />;

  const featuredRewards = [
    GACHA_REWARDS.find((reward) => reward.id === "stars-3")!,
    GACHA_REWARDS.find((reward) => reward.id === "moon-lamp")!,
    GACHA_REWARDS.find((reward) => reward.id === "starlight-dress")!,
    GACHA_REWARDS.find((reward) => reward.id === "toy-telescope")!
  ];

  return (
    <section
      className={"surprise-gacha-page page-view machine-" + stage}
      style={{ "--gacha-background": `url(${assets.gachaSkyBg})` } as React.CSSProperties}
    >
      <div className="surprise-brand"><Link to="/" aria-label="返回首页">‹</Link><img src={assets.logo} alt="Lusky 鹿屿森空" /></div>
      <header className="surprise-heading">
        <p><i>✦</i> 今日小惊喜 <i>✦</i></p>
        <h1>星芽扭蛋机</h1>
      </header>

      <div className="surprise-status">
        <div><img src={assets.gachaRewardStars} alt="" /><span>今日机会<b>{Math.max(0, 3 - daily.drawsUsed)} / 3</b></span></div>
        <div><img src={assets.gachaRewardStars} alt="" /><span>我的星光<b>{stars}</b></span></div>
      </div>

      <main className="gacha-stage">
        <section className="gacha-machine-zone" aria-label="星芽扭蛋机互动区">
          <div className="lumi-rig" aria-label="露米正在挥手">
            <span>嘿嘿！<br />今天会抽到什么呢？</span>
            <img className="rig-body static-wave" src={assets.lumiGachaStatic} alt="露米向你挥手" />
            <b>Lumi ✦</b>
          </div>

          <div className="gacha-machine-art">
            <img className="capsule-cluster-render" src={assets.gachaCapsuleCluster} alt="扭蛋机里的彩色星光扭蛋" />
            <img className="machine-render" src={assets.gachaMachine} alt="蓝金星芽扭蛋机" />
            <button
              className="machine-lever"
              type="button"
              style={{ "--lever-angle": leverAngle + "deg" } as React.CSSProperties}
              disabled={busy || stage !== "idle"}
              onPointerDown={onLeverDown}
              onPointerMove={onLeverMove}
              onPointerUp={onLeverUp}
              onPointerCancel={() => { dragStart.current = null; setLeverAngle(0); }}
              aria-label="向下拨动扭蛋机摇杆"
            ><img src={assets.gachaLever} alt="" /><small>向下拨</small></button>
            <button
              className={"dropped-capsule " + (stage === "ready" || stage === "opening" ? "is-ready" : "")}
              type="button"
              onClick={() => void openCapsule()}
              disabled={stage !== "ready"}
              aria-label="打开掉落的扭蛋"
            ><img src={assets.gachaCapsuleClosed} alt="等待打开的星光扭蛋" /></button>
          </div>

          <div className="aduo-rig" aria-label="阿朵正在为你加油">
            <span>{message}</span>
            <img className="rig-body static-wave" src={assets.aduoGachaStatic} alt="阿朵向你挥手" />
            <b>Aduo 🐾</b>
          </div>
        </section>

        <aside className="gacha-pool">
          <header><i>★</i><h2>今日奖池</h2><i>★</i></header>
          <div className="reward-preview-grid">
            {featuredRewards.map((reward) => (
              <article key={reward.id} className={"rarity-" + reward.rarity}>
                <em>★</em>
                <RewardVisual reward={reward} compact />
                <span>{reward.name}</span>
              </article>
            ))}
          </div>
          <button type="button" onClick={() => setView("letter")}>✉ 打开星光信件</button>
        </aside>
      </main>

      <footer className="gacha-actions">
        <button className="catalog-button" type="button" onClick={() => setMessage("奖品会自动收进我的小屋，已经拥有的收藏品不会重复出现。")}>▤ 查看奖品图鉴</button>
        <button className="draw-button" type="button" onClick={() => void beginDraw()} disabled={loading || busy || stage !== "idle"}>
          <span>★</span>{loading ? "正在点亮…" : daily.drawsUsed >= 3 ? "今天已完成" : "转动扭蛋"}<span>★</span>
        </button>
        <Link className="room-button" to="/room">⌂ 我的小屋</Link>
        <p>★ 每天 3 次，把惊喜带回小屋 ★</p>
      </footer>
      {(stage === "opening" || stage === "revealed") && (
        <div className={"reward-reveal " + (stage === "revealed" ? "is-visible" : "")} role="dialog" aria-modal="true" aria-label="扭蛋奖励">
          <div className="confetti" aria-hidden="true">{Array.from({ length: 20 }, (_, index) => <i key={index} />)}</div>
          <img className="reward-capsule-open" src={assets.gachaCapsuleOpen} alt="打开的星光扭蛋" />
          {revealedReward && (
            <article className={"reward-card rarity-" + revealedReward.rarity}>
              <p>{revealedReward.rarity === "magic" ? "魔法惊喜" : revealedReward.rarity === "rare" ? "闪亮惊喜" : "今日惊喜"}</p>
              <RewardVisual reward={revealedReward} />
              <h2>{revealedReward.name}</h2>
              <span>{revealedReward.description}</span>
              {revealedReward.type === "stars" && <b>＋{revealedReward.quantity} 颗</b>}
              <button type="button" onClick={finishReveal}>{daily.drawsUsed >= 3 ? "把惊喜收好" : "再扭一次 · 剩余 " + (3 - daily.drawsUsed) + " 次"}</button>
            </article>
          )}
        </div>
      )}
    </section>
  );
}